import { HtmlHTMLAttributes, memo, MouseEventHandler, useCallback, useRef, useState } from "react";
import classNames from "classnames";
import { animated, config, useTransition } from "react-spring";
import { PopperInstance, Tooltip } from "../../ui";

import { ReactComponent as CopyIcon } from "./icons/copy_icon.svg";
import { ReactComponent as CopySuccessIcon } from "./icons/copy_success_icon.svg";

import styles from "./styles.module.scss";

export type CopyTextProps = {
  text: string;
  copyTooltipText?: string;
  copySuccessTooltipText?: string;
  iconClassName?: string;
  tooltipClassName?: string;
} & HtmlHTMLAttributes<HTMLSpanElement>

export const CopyText = memo<CopyTextProps>((
  {
    children,
    text,
    className,
    copyTooltipText = "Copy",
    copySuccessTooltipText = "Copied!",
    tooltipClassName,
    iconClassName,
    ...rest
  }) => {
  const [ copied, setCopied ] = useState<boolean>(false);
  const [ hovered, setHovered ] = useState<boolean>(false);
  const [ isTooltipOpen, setIsTooltipOpen ] = useState<boolean>(false);
  const tooltip = useRef<PopperInstance>(null);

  const transitions = useTransition(hovered, {
    from: { x: -25, opacity: 0, width: 0 },
    enter: { x: 3, opacity: 1, width: 25 },
    leave: { x: -25, opacity: 0, width: 0 },
    onChange: () => tooltip?.current?.update?.(),
    config: config.stiff,
  });

  const onCopy: MouseEventHandler<HTMLSpanElement> = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      setCopied(true);
      navigator?.clipboard?.writeText?.(text);
    }, [ text ]);
  const wrappedOnDoubleClick: MouseEventHandler<HTMLSpanElement> = useCallback(async (e) => {
    setIsTooltipOpen(true);
    await setTimeout(() => setIsTooltipOpen(false), 100);
    onCopy(e);
    rest?.onDoubleClick?.(e);
  }, [ rest, onCopy ]);
  const onMouseEnter: MouseEventHandler<HTMLSpanElement> = useCallback((e) => {
    setHovered(true);
    rest?.onMouseEnter?.(e);
  }, [ setHovered, rest ]);
  const onMouseLeave: MouseEventHandler<HTMLSpanElement> = useCallback(async (e) => {
    setHovered(false);
    await setTimeout(() => setCopied(false), 200);
    rest?.onMouseLeave?.(e);
  }, [ setHovered, rest ]);

  return (
    <span
      onDoubleClick={wrappedOnDoubleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={classNames(styles.root, className)}
      {...rest}
    >
      {children ?? text}
      {transitions((style, hovered) => (
        hovered &&
        <Tooltip
          arrow={false}
          className={classNames(styles.tooltip, tooltipClassName)}
          placement="top"
          delay={10}
          externalIsOpen={isTooltipOpen || copied}
          setExternalIsOpen={setIsTooltipOpen}
          ref={tooltip}
          content={copied ? copySuccessTooltipText : copyTooltipText}
        >
          <animated.button
            style={style}
            type="button"
            onClick={onCopy}
            className={classNames(styles.icon, iconClassName)}
          >
            {copied ? <CopySuccessIcon /> : <CopyIcon />}
          </animated.button>
        </Tooltip>
      ))}
    </span>
  );
});

export default CopyText;